import React, { useEffect, useMemo, useState } from 'react';
import { format, startOfMonth } from 'date-fns';
import { Controller } from 'react-hook-form';
import { Col, Input, Row } from 'reactstrap';
import Datepicker from 'components/Inputs/DatePicker';
import { useUsers } from 'views/Report/Protocol/hooks/useUsers';
import { useListLeadStagesByRecordType } from 'views/CRM/LeadStages/hooks/useListLeadStagesByRecordType';
import FlexSelect from 'components/Inputs/FlexSelect';
import { timeDataNames } from 'views/CRM/shared/utils/leadOpportunityTimeName';
import { leadTypes } from 'views/CRM/shared/utils/leadTypes';
import { useGetLostReasonByEntity } from 'views/CRM/hooks/useGetLostReasonByEntity';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';

export const dateRules = {
  required: 'Data é obrigatória',
  pattern: {
    value: /^[0-3][0-9]\/[0-1][0-9]\/[0-9][0-9][0-9][0-9]$/,
    message: 'Data inválida! (dd/MM/aaaa)'
  }
};
export const getDateBegin = () => {
  const date = new Date();
  const firstDayMonth = new Date(
    date.getFullYear('pt-br'),
    date.getMonth('pt-br'),
    1
  );
  return format(startOfMonth(firstDayMonth), 'dd/LL/yyyy');
};
export const toISOFormat = (dateTimeString) => {
  const [date] = dateTimeString.split(' ');
  const [DD, MM, YYYY] = date.split('/');
  return `${DD}/${MM}/${YYYY}`;
};

const DefaultFieldsFilterForm = ({
  control,
  setValue,
  getValues,
  recordTypeId,
  register
}) => {
  const { userList } = useUsers();
  const [filterListLostReason, setFilterListLostReason] = useState([]);
  const { listLostReason } = useGetLostReasonByEntity({
    entity: entitiesName.LEAD,
    recordTypeId
  });
  const { leadStages, getLeadStagesByRecordType, isLoadingLeadStages } =
    useListLeadStagesByRecordType();

  const showAllLeads = useMemo(() => {
    const flag = localStorage.getItem('show_all_leads');
    return flag === 'true';
  }, []);
  useEffect(() => {
    if (recordTypeId) getLeadStagesByRecordType(recordTypeId);
  }, [recordTypeId, getLeadStagesByRecordType]);

  useEffect(() => {
    if (!listLostReason) return;

    const options = listLostReason[0]?.options || [];
    const formattedOptions = options
      .filter((option) => option.recordTypeIds.includes(Number(recordTypeId)))
      .map((option) => ({ label: option.value, value: option.value }));

    setFilterListLostReason(formattedOptions);
  }, [listLostReason]);
  return (
    <>
      <Row className="mb-3">
        <Col sm="6">
          <label className="form-control-label">Buscar</label>
          <Controller
            as={
              <Input
                type="text"
                name="textQuery"
                placeholder="Buscar por nome, telefone ou email"
              />
            }
            control={control}
            name="textQuery"
            defaultValue=""
          />
        </Col>
        <Col sm="3">
          <label className="form-control-label">Fase do Lead</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={leadStages ? leadStages : []}
                isMulti={true}
                value={props.value || []}
                valueController={setValue}
                fieldName="stageId"
                labelName="name"
                valueName="id"
                isClearable={true}
                disabled={isLoadingLeadStages}
                closeMenuOnSelect={true}
              />
            )}
            control={control}
            defaultValue=""
            name="stageId"
          />
        </Col>
        <Col sm="3">
          <label className="form-control-label">Tipo de Lead</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={leadTypes || []}
                isMulti={true}
                value={props.value || []}
                valueController={setValue}
                fieldName="type"
                labelName="name"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
              />
            )}
            control={control}
            defaultValue={null}
            name="type"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col sm={showAllLeads ? '3' : '6'}>
          <label className="form-control-label">Data inicial</label>
          <Controller
            render={(props) => (
              <Datepicker
                fieldName="dateBegin"
                valueController={setValue}
                defaultValue={props.value}
              />
            )}
            control={control}
            name="dateBegin"
            rules={dateRules}
            defaultValue={getDateBegin()}
          />
        </Col>
        <Col sm={showAllLeads ? '3' : '6'}>
          <label className="form-control-label">Data final</label>
          <Controller
            render={(props) => (
              <Datepicker
                fieldName="dateEnd"
                valueController={setValue}
                defaultValue={props.value}
              />
            )}
            control={control}
            name="dateEnd"
            defaultValue={new Date().toLocaleDateString('pt-br')}
            rules={{
              ...dateRules,
              validate: async (value) => {
                if (
                  new Date(toISOFormat(value)) <
                  new Date(toISOFormat(getValues()['dateBegin']))
                ) {
                  return 'A data final não pode ser anterior à inicial!';
                } else {
                  return true;
                }
              }
            }}
          />
        </Col>
        {showAllLeads && (
          <Col sm="3">
            <label className="form-control-label">Responsável</label>
            <Controller
              render={(props) => (
                <FlexSelect
                  dataOptions={userList || []}
                  isMulti={true}
                  value={props.value || []}
                  valueController={setValue}
                  fieldName="responsibleLogin"
                  labelName="name"
                  valueName="login"
                  isClearable={true}
                  closeMenuOnSelect={true}
                />
              )}
              control={control}
              name="responsibleLogin"
              defaultValue=""
            />
          </Col>
        )}
        <Col sm="3">
          <label className="form-control-label">Prazo do Lead</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={timeDataNames || []}
                isMulti={true}
                value={props.value || []}
                valueController={setValue}
                fieldName="leadTime"
                labelName="name"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
              />
            )}
            control={control}
            name="leadTime"
            defaultValue=""
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col sm="3">
          <label className="form-control-label">Canal de origem</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={[
                  { name: 'WhatsApp', value: 'whatsapp' },
                  { name: 'Email', value: 'email' },
                  { name: 'Chat', value: 'chat' },
                  { name: 'Telefone', value: 'telefone' },
                  { name: 'Webchat', value: 'webchat' }
                ]}
                isMulti={false}
                value={props.value}
                valueController={setValue}
                fieldName="channelOrigin"
                labelName="name"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
              />
            )}
            control={control}
            defaultValue={null}
            name="channelOrigin"
          />
        </Col>
        <Col sm="3">
          <label className="form-control-label">Gerou Oportunidade</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={[
                  { name: 'Sim', value: true },
                  { name: 'Não', value: false }
                ]}
                isMulti={false}
                value={props.value}
                valueController={setValue}
                fieldName="isConverted"
                labelName="name"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
              />
            )}
            control={control}
            defaultValue={null}
            name="isConverted"
          />
        </Col>
        <Col sm="3">
          <label className="form-control-label">Motivo de perda</label>
          <Controller
            render={(props) => (
              <FlexSelect
                isClearable={true}
                placeholder="Selecione o motivo"
                dataOptions={filterListLostReason || []}
                isMulti={true}
                closeMenuOnSelect={true}
                value={props.value || []}
                valueController={setValue}
                fieldName="lostReason"
                labelName="value"
                valueName="value"
              />
            )}
            control={control}
            name="lostReason"
            defaultValue={null}
          />
        </Col>
        <Col sm="3">
          <label className="form-control-label">Gerado Manualmente</label>
          <Controller
            render={(props) => (
              <FlexSelect
                dataOptions={[
                  { name: 'Sim', value: true },
                  { name: 'Não', value: false }
                ]}
                isMulti={false}
                value={props.value}
                valueController={setValue}
                fieldName="isManual"
                labelName="name"
                valueName="value"
                isClearable={true}
                closeMenuOnSelect={true}
              />
            )}
            control={control}
            defaultValue={null}
            name="isManual"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col sm="6">
          <label
            className="form-control-label"
            style={{ marginRight: '20px', fontWeight: '400' }}
          >
            <input
              type="radio"
              name="filterDateBy"
              ref={register}
              value="creation"
              defaultChecked={true}
            />{' '}
            Data de criação
          </label>
          <label className="form-control-label" style={{ fontWeight: '400' }}>
            <input
              type="radio"
              name="filterDateBy"
              ref={register}
              value="last_update"
              defaultChecked={false}
            />{' '}
            Data de última alteração
          </label>
        </Col>
      </Row>
    </>
  );
};

export default DefaultFieldsFilterForm;
